import React, { useState } from 'react';
import { FormSwitch } from 'components/form';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { GetPartnerLinksResponse } from 'modules/aftermarket';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Paper, Stack, Button, TextField, Typography, Autocomplete, CircularProgress, Box } from '@mui/material';

interface Props {
  canWrite: boolean;
  selectedVendor: any[];
  setSelectedVendor: any;
  isPartnerAccount: boolean;
  isPartnerLinksLoading: boolean;
  partnerLinksResponse: GetPartnerLinksResponse | undefined;
}

export const VendorSection = ({
  canWrite,
  selectedVendor,
  isPartnerAccount,
  setSelectedVendor,
  isPartnerLinksLoading,
  partnerLinksResponse,
}: Props) => {
  const [isShowOnlyActive, setIsShowOnlyActive] = useState<boolean>(true);

  const availableVendors = (partnerLinksResponse?.aftermarketDealerPartnerLink ?? [])
    .toSorted((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    .map(({ name, partnerLinkId, partnerProfile, dealerMetaData }) => ({
      value: partnerLinkId,
      isActive: dealerMetaData?.isActive ?? true,
      label: `${partnerProfile.availableToPro ? '[PRO]' : ''} ${name || 'Partner Vendor'}`,
    }));

  const availableVendorsOptions = availableVendors.filter(({ isActive }) => (isShowOnlyActive ? isActive : true));

  return (
    <Paper sx={{ padding: '1rem' }}>
      <Stack gap="0.5rem">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Typography variant="subtitle1" color="primary">
            <LooksOneIcon /> {isPartnerAccount ? 'Dealer' : 'Aftermarket Vendor'}
          </Typography>

          <RenderWithCondition condition={isPartnerLinksLoading}>
            <CircularProgress size={20} />
          </RenderWithCondition>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
          <Autocomplete
            fullWidth
            multiple
            size="small"
            options={availableVendorsOptions}
            getOptionLabel={option => option.label}
            disabled={!canWrite || isPartnerLinksLoading}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, newValues) => setSelectedVendor(newValues)}
            value={availableVendors.filter((vendor: any) => selectedVendor.find(sv => sv.value === vendor.value))}
            renderInput={params => (
              <TextField
                {...params}
                required
                variant="outlined"
                label={`Select the ${isPartnerAccount ? 'Dealers that have access to this product' : 'Vendor'}`}
              />
            )}
          />

          <RenderWithCondition condition={isPartnerAccount}>
            <Button
              variant="contained"
              onClick={() => setSelectedVendor(availableVendorsOptions)}
              disabled={!canWrite || isPartnerLinksLoading || !availableVendorsOptions.length}
            >
              Add All
            </Button>
          </RenderWithCondition>
        </Box>

        <RenderWithCondition condition={isPartnerAccount}>
          <FormSwitch
            disabled={!canWrite}
            label="Show only active"
            checked={isShowOnlyActive}
            onChange={(_, checked) => {
              setIsShowOnlyActive(checked);
            }}
          />
        </RenderWithCondition>
      </Stack>
    </Paper>
  );
};
